import { useCallback, useEffect, useState } from 'react';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { showSnackbar } from '@backrs_ui/grow-ui/src/redux/appState/appState';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  getActiveLubavSession,
  getSessionState,
} from '@backrs_ui/grow-ui/src/redux/lubavSessionState/lubavSessionState.utils';
import {
  selectBoosterReportContent,
  selectBoosterReportFacts,
  selectBoosterReportRequestFloor,
  selectBoosterStep,
  setBoosterReportContent,
} from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.slice';
import { ReportFacts } from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.types';

import { findReportRequestIndex } from '../utils/reportRequest';

const STRINGS = appStrings.modules.satActBooster;

const REPORT_TIMEOUT_MS = 20_000;
const REPORT_DEADLINE_MS = 120_000;
// Held after the report lands so the stream finishing and the wizard opening aren't the same beat.
const REPORT_LINGER_MS = 2_000;

type GiveUpReason = 'stream-failed' | 'dead-wait' | 'deadline';

/**
 * Captures this run's report out of the chat, and says whether the seam is still waiting on it. The
 * report is the turn answering the request, told apart from an error or aborted bubble by its
 * response id. Waiting ends on a failed turn, a dead wait or the deadline as well as on success.
 *
 * `reportReady` marks the held beat after a successful landing; `awaitingReport` stays true for
 * its duration.
 */
export const useBoosterReport = (): { awaitingReport: boolean; reportReady: boolean } => {
  const dispatch = useAppDispatch();
  const activeSession = useAppSelector(getActiveLubavSession);
  const messages = useAppSelector(
    (state) => getSessionState(state, activeSession).coachLubav.officeHoursMessages,
  );
  const isStreaming = useAppSelector(
    (state) => getSessionState(state, activeSession).coachLubav.streaming,
  );
  const reportFacts = useAppSelector(selectBoosterReportFacts);
  const reportContent = useAppSelector(selectBoosterReportContent);
  const requestFloor = useAppSelector(selectBoosterReportRequestFloor);
  const step = useAppSelector(selectBoosterStep);

  // Keyed on the facts object: fresh per request, so one give-up can't skip the next wait.
  const [releasedFor, setReleasedFor] = useState<ReportFacts | null>(null);
  const [lingering, setLingering] = useState(false);

  const requestIndex = requestFloor === null ? -1 : findReportRequestIndex(messages, requestFloor);
  // This run's report lands directly after its request and nowhere else: a completed turn rebuilds
  // the transcript that way, and a failed one drops its error bubble in the same slot.
  const nextTurn = requestIndex === -1 ? undefined : messages[requestIndex + 1];
  const answer = nextTurn?.sender === 'ai' ? nextTurn : undefined;

  useEffect(() => {
    if (reportContent) return;
    // The report is queued as the last score step advances, and streams for seconds after —
    // Targets alone would close the window on a student who clicks through it before it lands.
    if (step !== 'TARGETS' && step !== 'SCHEDULE') return;

    if (!answer?.id || !answer.text) return;

    dispatch(setBoosterReportContent(answer.text));
    // Only Targets sits behind the loader; a student already on Schedule has nothing to hold.
    if (step === 'TARGETS') setLingering(true);
  }, [answer, reportContent, step, dispatch]);

  useEffect(() => {
    if (!lingering) return;

    const timeout = setTimeout(() => setLingering(false), REPORT_LINGER_MS);

    return () => clearTimeout(timeout);
  }, [lingering]);

  // Not gated on the request floor: that lands a frame later and would flash Targets first.
  const pending = step === 'TARGETS' && reportFacts !== null && !reportContent;
  const streamFailed = !isStreaming && answer !== undefined && !answer.id;

  const released = releasedFor === reportFacts;
  const giveUp = useCallback(
    (reason: GiveUpReason) => {
      console.error('[test-booster] score report unavailable', { reason });
      setReleasedFor(reportFacts);
      dispatch(showSnackbar({ message: STRINGS.report.streamFailed, severity: 'error' }));
    },
    [reportFacts, dispatch],
  );

  useEffect(() => {
    if (pending && !released && streamFailed) giveUp('stream-failed');
  }, [pending, released, streamFailed, giveUp]);

  // No timer while streaming: a long report is still a working one.
  useEffect(() => {
    if (!pending || released || isStreaming) return;

    const timeout = setTimeout(() => giveUp('dead-wait'), REPORT_TIMEOUT_MS);

    return () => clearTimeout(timeout);
  }, [pending, released, isStreaming, giveUp]);

  // The chat owns `streaming` and has paths that never reset it, disarming both releases above.
  useEffect(() => {
    if (!pending || released) return;

    const deadline = setTimeout(() => giveUp('deadline'), REPORT_DEADLINE_MS);

    return () => clearTimeout(deadline);
  }, [pending, released, giveUp]);

  return {
    awaitingReport: (pending || lingering) && !released,
    reportReady: lingering && !released,
  };
};
